/**
 * Light highlights and vegetation controls of the Player's ☀ panel (P105): the same values as the
 * viewer's debug panel (Light & exposure, Vegetation), saved per device like the look settings. They
 * reach the viewer as URL parameters when a walk opens and through `walkbake.set` while it is open.
 * Ranges must match the viewer (public/viewer/app/main.js, LEAF_OPTIONS in vertexLight.js); defaults
 * are the values Santi picked on the house walk (2026-11-03).
 *
 * Pure functions apart from storage, so they run under node:test.
 */

import type { WalkbakeApi } from "./exposureSettings.ts";
import { clampRound, type NumericRange } from "./numeric.ts";

export interface PlantLightSettings {
  /** Specular highlights from the scene's lamps on/off (viewer `lights`). */
  lights: boolean;
  /** Strength of those highlights, 0..2 (viewer `lightIntensity`). */
  lightIntensity: number;
  vegetationLight: number;
  leafShine: number;
  leafDetail: number;
  leafHighlights: number;
  leafTranslucency: number;
  sunGlow: number;
}

export type PlantLightNumber = Exclude<keyof PlantLightSettings, "lights">;

export const PLANT_LIGHT_RANGES: Record<PlantLightNumber, NumericRange> = {
  lightIntensity: { min: 0, max: 2, step: 0.01 },
  vegetationLight: { min: 0, max: 4, step: 0.01 },
  leafShine: { min: 0, max: 2, step: 0.01 },
  leafDetail: { min: 0, max: 2, step: 0.01 },
  leafHighlights: { min: 0, max: 1, step: 0.01 },
  leafTranslucency: { min: 0, max: 4, step: 0.01 },
  sunGlow: { min: 0, max: 4, step: 0.01 },
};

export const DEFAULT_PLANT_LIGHT_SETTINGS: PlantLightSettings = {
  lights: true,
  lightIntensity: 0.1,
  vegetationLight: 1.15,
  leafShine: 1.45,
  leafDetail: 2,
  leafHighlights: 0.6,
  leafTranslucency: 0.85,
  sunGlow: 2.15,
};

/** Viewer URL parameter for each value. */
const QUERY_KEYS: Record<keyof PlantLightSettings, string> = {
  lights: "lights",
  lightIntensity: "light_intensity",
  vegetationLight: "vegetationLight",
  leafShine: "leafShine",
  leafDetail: "leafDetail",
  leafHighlights: "leafHighlights",
  leafTranslucency: "leafTranslucency",
  sunGlow: "sunGlow",
};

const STORAGE_KEY = "walk-player.plant-light";

function sanitize(raw: Partial<Record<keyof PlantLightSettings, unknown>>, fallback: PlantLightSettings): PlantLightSettings {
  const result = { ...fallback, lights: typeof raw.lights === "boolean" ? raw.lights : fallback.lights };
  for (const key of Object.keys(PLANT_LIGHT_RANGES) as PlantLightNumber[]) {
    const value = raw[key];
    if (typeof value === "number" && Number.isFinite(value)) result[key] = clampRound(PLANT_LIGHT_RANGES[key], value);
  }
  return result;
}

export function loadPlantLightSettings(): PlantLightSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return sanitize(JSON.parse(raw) as Partial<Record<keyof PlantLightSettings, unknown>>, DEFAULT_PLANT_LIGHT_SETTINGS);
  } catch {
    // Blocked or corrupt storage: fall back to defaults.
  }
  return { ...DEFAULT_PLANT_LIGHT_SETTINGS };
}

export function savePlantLightSettings(settings: PlantLightSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Not persisted; still applies for this session.
  }
}

/** Overrides from the Player's own URL (same names as the viewer's), for tests: win for that page, not saved. */
export function withPlantLightUrlOverrides(settings: PlantLightSettings, search: string): PlantLightSettings {
  const params = new URLSearchParams(search);
  const raw: Partial<Record<keyof PlantLightSettings, unknown>> = {};
  for (const [key, name] of Object.entries(QUERY_KEYS) as [keyof PlantLightSettings, string][]) {
    const value = params.get(name);
    if (value === null || value.trim() === "") continue;
    raw[key] = key === "lights" ? value !== "0" : Number(value);
  }
  return sanitize(raw, settings);
}

/** Query string for the viewer page. */
export function plantLightQuery(settings: PlantLightSettings): string {
  const params = new URLSearchParams();
  for (const [key, name] of Object.entries(QUERY_KEYS) as [keyof PlantLightSettings, string][]) {
    params.set(name, key === "lights" ? (settings.lights ? "1" : "0") : String(settings[key]));
  }
  return params.toString();
}

/** Pushes changed values into an open viewer (`walkbake.set` uses the same names as the settings). */
export function applyPlantLightToViewer(api: WalkbakeApi | null, patch: Partial<PlantLightSettings>): void {
  if (!api || Object.keys(patch).length === 0) return;
  api.set(patch as Record<string, unknown>);
}
